import { Group, Stack, Tooltip, Text, Menu } from '@mantine/core';
import {
  BagelAiIcon,
  FromBagelIcon,
  RequestIcon,
} from '../../../icons/x-symbol-svgrepo-com';
import { IconAlertOctagon, IconCheck, IconCoin, IconComponents } from '@tabler/icons';
import { InfoTooltip } from '../../common/InfoTooltip';
import { useContext, useEffect, useMemo, useState } from 'react';
import { saveRequest } from '../../../Api';
import { useAuth0 } from '@auth0/auth0-react';
import { showNotification } from '@mantine/notifications';
import IdeaContext from '../../ideaNiv/IdeaContext';
import { getNestedValue } from '../../ideaNiv/Funcs';
import { formatBigMonetaryValueIdeas } from '../../ideas/ideas-table-helpers/Helpers';
import { useGroupedAggregations } from '../../../hooks/ideaPage/useIdeaQueries';
import { useFields } from '../../../hooks/useFields';
import { useIdeaFilters } from '../../../hooks/useIdeaFilters';
import { aggregationIdsForCardAttributes } from '../../../utils/evidence/aggregationIds';
import { selectFirstAggregationRow } from '../../../utils/evidence/aggregationResponse';
import { getOrgExtraInfoFieldsFromFiltersConfig, ORG_EXTRA_INFO_FIELDS_LIMIT_DEFAULT } from '../../../utils/org-filter-config';
import { getOrgExtraInfoBadgeItems } from '../../../utils/company-filter-label';
import { resolveTablerIcon } from '../../common/CompanyFilterExtraInfoBadges';

/**
 * Discovery/Explore: inbox-style wrapping attribute pills (does not modify legacy CardAttributes).
 */
export function DiscoveryCardAttributes({
  item,
  loadEvidence: propLoadEvidence,
}: {
  item: any;
  loadEvidence?: () => void;
}) {
  const auth0 = useAuth0();
  const { requestPriorities, allComponents, impactAggregationProperties } = useFields();

  const impactsToShow =
    impactAggregationProperties?.filter(
      (a: any) => a?.impact?.displayInEvidenceCard !== false
    ) || [];

  const contextData = useContext(IdeaContext);
  const { loadEvidence: contextLoadEvidence } = contextData;

  const finalLoadEvidence = propLoadEvidence || contextLoadEvidence;

  const [priority, setPriority] = useState<string>(item?.priority || 'Unassigned');
  const [color, setColor] = useState('');

  const sourceOfAssignment = item?.sourceOfAssignment;
  const company = item?.company || null;
  const companyName = company?.name;

  const { ideasFiltersConfig } = useIdeaFilters();
  const orgExtraInfoFields = useMemo(
    () => getOrgExtraInfoFieldsFromFiltersConfig(ideasFiltersConfig?.evidence, ORG_EXTRA_INFO_FIELDS_LIMIT_DEFAULT),
    [ideasFiltersConfig?.evidence]
  );
  const orgExtraInfoItems = useMemo(
    () => (company ? getOrgExtraInfoBadgeItems(company, orgExtraInfoFields) : []),
    [company, orgExtraInfoFields]
  );

  const onSelectSave = (type: string, value: string) => {
    let updatedValue = value;

    if (type === 'componentId') {
      const found = allComponents.find((a: any) => a._id === value);
      updatedValue = found?.name;
    }

    if (item) {
      const itemObj = {
        id: item?._id,
        [type]: value,
      };

      saveRequest(itemObj, auth0)
        .then(() => {
          finalLoadEvidence();
          showNotification({
            title: `${
              type === 'componentId'
                ? 'Product area'
                : type === 'priority'
                  ? 'Priority'
                  : 'Domain'
            } updated`,
            color: 'teal',
            message: `${
              type === 'componentId'
                ? 'Product area'
                : type === 'priority'
                  ? 'Priority'
                  : 'Domain'
            } updated to "${updatedValue}" successfully`,
            icon: <IconCheck size="1.1rem" />,
            autoClose: 1200,
          });
        })
        .catch((e: any) => console.log(e));
    }
  };

  const evidenceIds = useMemo(() => aggregationIdsForCardAttributes(item), [item]);
  const aggregationsQuery = useGroupedAggregations(
    evidenceIds.length > 0 ? evidenceIds : undefined
  );

  const evidenceAggregations = useMemo(
    () => selectFirstAggregationRow(aggregationsQuery?.data ?? null),
    [aggregationsQuery?.data]
  );

  useEffect(() => {
    setPriority(item?.priority || 'Unassigned');
    const currentPriorityIndex = requestPriorities.findIndex(
      (r: any) => r.key.toLowerCase() === item?.priority?.toLowerCase()
    );
    if (currentPriorityIndex === -1) {
      setPriority('Unassigned');
    }
    setColor(requestPriorities[currentPriorityIndex]?.color);
  }, [item, requestPriorities]);

  const priorityIndex = useMemo(
    () =>
      requestPriorities.findIndex(
        (r: any) => r.key.toLowerCase() === item?.priority?.toLowerCase()
      ),
    [item?.priority, requestPriorities]
  );
  const priorityBg =
    priorityIndex >= 0 ? requestPriorities[priorityIndex]?.bgColor : '#F8F9FB';

  return (
    <Group pb={8} spacing={8} sx={{ flexWrap: 'wrap', rowGap: 8 }} align="flex-start">
      {sourceOfAssignment === 'suggestion' ? (
        <Tooltip label="Found by Bagel AI">
          <Stack h={20} w={20} align="center">
            <BagelAiIcon />
          </Stack>
        </Tooltip>
      ) : sourceOfAssignment === 'bagel' || sourceOfAssignment === 'search' ? (
        <Tooltip label="Added by PM">
          <Stack align="center" h={20} w={20}>
            <FromBagelIcon />
          </Stack>
        </Tooltip>
      ) : sourceOfAssignment === 'request' ? (
        <Tooltip label="Request">
          <Stack align="center" h={20} w={20}>
            <RequestIcon />
          </Stack>
        </Tooltip>
      ) : null}

      <InfoTooltip label="Customer" icon={<IconComponents stroke={1} color="black" size={16} />}>
        {item?.isContainer && item?.hasChildren
          ? `${item?.customersCount} CUSTOMERS`
          : companyName || 'N/A'}
      </InfoTooltip>

      {!(item?.isContainer && item?.hasChildren) &&
        orgExtraInfoItems.map((extraInfoItem, index) => {
          const ExtraInfoIcon = resolveTablerIcon(extraInfoItem.icon);
          return (
            <InfoTooltip
              key={`${extraInfoItem.tooltip || 'extra-info'}-${index}`}
              label={extraInfoItem.tooltip || 'Attribute'}
              icon={ExtraInfoIcon ? <ExtraInfoIcon size={16} /> : null}
            >
              {extraInfoItem.displayValue}
            </InfoTooltip>
          );
        })}

      {impactsToShow &&
        impactsToShow.length > 0 &&
        impactsToShow.map((impactItem: any) => {
          const valueText =
            item?.isContainer && evidenceAggregations
              ? impactItem.propertyName
                ? formatBigMonetaryValueIdeas(
                  (evidenceAggregations as Record<string, { total: number }>)[
                    impactItem.propertyName
                  ]?.total,
                  impactItem?.impact?.prefix || undefined
                )
                : 'N/A'
              : impactItem.fieldPath && getNestedValue(item, impactItem.fieldPath)
                ? formatBigMonetaryValueIdeas(
                  getNestedValue(item, impactItem.fieldPath),
                  impactItem?.impact?.prefix || undefined
                )
                : 'N/A';
          return (
            <InfoTooltip
              key={impactItem?.label || impactItem?.fieldPath}
              label={impactItem?.label || 'Impact'}
              icon={<IconCoin color="black" size={18} />}
            >
              {valueText}
            </InfoTooltip>
          );
        })}

      <Menu shadow="sm" withArrow>
        <Menu.Target>
          <Tooltip label="Change priority">
            <Group
              spacing={6}
              h={32}
              px="sm"
              noWrap
              sx={{
                cursor: 'pointer',
                borderRadius: 5,
                background: priorityBg,
                color: color || '#212529',
                border: color ? `1px solid ${color}35` : '1px solid #e9ecef',
                alignItems: 'center',
              }}
            >
              <IconAlertOctagon size={18} color={color || '#868e96'} />
              <Text fz={14} sx={{ color: color || '#212529' }}>
                {priority || 'N/A'}
              </Text>
            </Group>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          {requestPriorities.map((priorityItem: any) => (
            <Menu.Item
              w="180px"
              key={priorityItem.label}
              onClick={() => {
                setPriority(priorityItem?.label);
                setColor(priorityItem.color);
                onSelectSave('priority', priorityItem.label);
              }}
            >
              {priorityItem.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
